import { useEffect, useState } from "react";
import {
  Table,
  Tag,
  Card,
  message,
  Button,
  Input,
  Space,
  Modal,
  Tabs,
  Descriptions,
  Divider,
} from "antd";
import { DownloadOutlined, EyeOutlined } from "@ant-design/icons";
import axiosInstance from "../../../configs";
import dayjs from "dayjs";
import "dayjs/locale/id";

dayjs.locale("id");

const { TabPane } = Tabs;

const IndexBrowse = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pagination & Filter State
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Filters state
  const [filters, setFilters] = useState({
    q: "", // Check if user wants global search or column search
    kodeDokumen: "",
    startDate: "",
    endDate: "",
  });

  const fetchData = async (params = {}) => {
    setLoading(true);
    try {
      // Merge current pagination and filters with new params
      const queryParams = {
        page: params.current || pagination.current,
        limit: params.pageSize || pagination.pageSize,
        q: params.q || filters.q,
        kodeDokumen: params.kodeDokumen || filters.kodeDokumen,
      };

      console.log("[Portal] Fetching data with params:", queryParams);
      const response = await axiosInstance.get(
        "/portal/pasifik/browse-data-header",
        {
          params: queryParams,
        },
      );
      console.log("[Portal] Response:", response.data);

      const responseData = response.data;
      if (responseData.data) {
        setData(responseData.data);
        setPagination({
          ...pagination,
          current: responseData.pagination.page,
          pageSize: responseData.pagination.limit,
          total: responseData.pagination.total,
        });
      } else {
        // Fallback if older API format (array only)
        setData(Array.isArray(responseData) ? responseData : []);
      }
    } catch (error) {
      console.error("[Portal] Fetch error:", error);
      message.error(
        "Gagal mengambil data: " +
          (error.response?.data?.error || error.message),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  const handleTableChange = (newPagination, filters, sorter) => {
    fetchData({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      // Add sorting logic if needed
    });
  };

  const handleSearch = (value) => {
    setFilters((prev) => ({ ...prev, q: value }));
    fetchData({ q: value, current: 1 }); // Reset to page 1 on search
  };

  // Detail Modal State
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState({
    header: {},
    barang: [],
    entitas: [],
    kemasan: [],
    kontainer: [],
    dokumen: [],
    pengangkut: [],
  });

  const handleDownloadPdf = async (record) => {
    // Implement PDF download logic here using proxy
    message.info("Fitur download PDF belum diimplementasikan sepenuhnya.");
  };

  const handleViewDetail = async (record) => {
    setDetailVisible(true);
    setDetailLoading(true);

    const nomorDaftar = record.nomor_daftar;
    if (!nomorDaftar) {
      message.error("Nomor Pendaftaran tidak ditemukan.");
      setDetailLoading(false);
      return;
    }

    const tanggalDaftar = record.tanggal_daftar
      ? dayjs(record.tanggal_daftar).format("YYYY-MM-DD")
      : null;
    if (!tanggalDaftar) {
      message.error("Tanggal Pendaftaran tidak ditemukan.");
      setDetailLoading(false);
      return;
    }

    try {
      const [
        headerRes,
        barangRes,
        entitasRes,
        kemasanRes,
        kontainerRes,
        dokumenRes,
        pengangkutRes,
      ] = await Promise.all([
        axiosInstance.get(`/portal/pasifik/header/${nomorDaftar}`),
        axiosInstance.get(`/portal/pasifik/get-data-barang`, {
          params: {
            nomorPendaftaran: nomorDaftar,
            tanggalPendaftaran: tanggalDaftar,
          },
        }),
        axiosInstance.get(`/portal/pasifik/get-data-entitas`, {
          params: {
            nomorPendaftaran: nomorDaftar,
            tanggalPendaftaran: tanggalDaftar,
          },
        }),
        axiosInstance.get(`/portal/pasifik/get-data-kemasan`, {
          params: {
            nomorPendaftaran: nomorDaftar,
            tanggalPendaftaran: tanggalDaftar,
          },
        }),
        axiosInstance.get(`/portal/pasifik/get-data-kontainer`, {
          params: {
            nomorPendaftaran: nomorDaftar,
            tanggalPendaftaran: tanggalDaftar,
          },
        }),
        axiosInstance.get(`/portal/pasifik/get-data-dokumen`, {
          params: {
            nomorPendaftaran: nomorDaftar,
            tanggalPendaftaran: tanggalDaftar,
          },
        }),
        axiosInstance.get(`/portal/pasifik/get-data-pengangkut`, {
          params: {
            nomorPendaftaran: nomorDaftar,
            tanggalPendaftaran: tanggalDaftar,
          },
        }),
      ]);

      const extractData = (res) => {
        if (res.data?.data && Array.isArray(res.data.data))
          return res.data.data;
        if (Array.isArray(res.data)) return res.data;
        return [];
      };

      setDetailData({
        header: headerRes.data?.data || headerRes.data || {},
        barang: extractData(barangRes),
        entitas: extractData(entitasRes),
        kemasan: extractData(kemasanRes),
        kontainer: extractData(kontainerRes),
        dokumen: extractData(dokumenRes),
        pengangkut: extractData(pengangkutRes),
      });
    } catch (error) {
      console.error("Error fetching details:", error);
      message.error("Gagal mengambil detail dokumen.");
    } finally {
      setDetailLoading(false);
    }
  };

  // --- Columns for Detail Tables ---
  const barangColumns = [
    { title: "Seri", dataIndex: "seri_barang", key: "seri_barang", width: 70 },
    { title: "HS Code", dataIndex: "hs", key: "hs", width: 120 },
    { title: "Uraian", dataIndex: "uraian", key: "uraian" },
    {
      title: "Jumlah",
      dataIndex: "jumlah_satuan",
      key: "jumlah_satuan",
      width: 100,
    },
    {
      title: "Satuan",
      dataIndex: "kode_satuan",
      key: "kode_satuan",
      width: 100,
    },
    {
      title: "CIF",
      dataIndex: "cif",
      key: "cif",
      width: 150,
      render: (val) =>
        val ? `Rp ${Number(val).toLocaleString("id-ID")}` : "-",
    },
  ];

  const entitasColumns = [
    {
      title: "Seri",
      dataIndex: "seri",
      key: "seri",
      width: 70,
    },
    {
      title: "Jenis",
      dataIndex: "kode_entitas",
      key: "kode_entitas",
      width: 100,
      render: (val) =>
        val === "3" ? "Importir" : val === "1" ? "Pemilik" : val,
    },
    {
      title: "Identitas",
      dataIndex: "nomor_identitas",
      key: "nomor_identitas",
      width: 150,
    },
    { title: "Nama", dataIndex: "nama_entitas", key: "nama_entitas" },
    { title: "Alamat", dataIndex: "alamat_entitas", key: "alamat_entitas" },
  ];

  const kemasanColumns = [
    {
      title: "Seri",
      dataIndex: "seri",
      key: "seri",
      width: 70,
    },
    {
      title: "Jenis",
      dataIndex: "kode_kemasan",
      key: "kode_kemasan",
    },
    { title: "Jumlah", dataIndex: "jumlah_kemasan", key: "jumlah_kemasan" },
    { title: "Merek", dataIndex: "merek", key: "merek" },
  ];

  const kontainerColumns = [
    {
      title: "Seri",
      dataIndex: "seri",
      key: "seri",
      width: 70,
    },
    { title: "Nomor", dataIndex: "nomor_kontiner", key: "nomor_kontiner" },
    {
      title: "Ukuran",
      dataIndex: "kode_ukuran_kontainer",
      key: "kode_ukuran_kontainer",
    },
    {
      title: "Tipe",
      dataIndex: "kode_tipe_kontainer",
      key: "kode_tipe_kontainer",
    },
  ];

  const dokumenColumns = [
    {
      title: "Seri",
      dataIndex: "seri",
      key: "seri",
      width: 70,
    },
    { title: "Kode", dataIndex: "kode_dokumen", key: "kode_dokumen" },
    { title: "Nomor", dataIndex: "nomor_dokumen", key: "nomor_dokumen" },
    {
      title: "Tanggal",
      dataIndex: "tanggal_dokumen",
      key: "tanggal_dokumen",
      render: (val) => (val ? dayjs(val).format("DD MMM YYYY") : "-"),
    },
  ];

  // Simplified columns without client-side filters
  const columns = [
    {
      title: "Kode",
      dataIndex: "kode_dokumen",
      key: "kode_dokumen",
      width: 70,
    },
    {
      title: "Nomor Pengajuan",
      dataIndex: "nomor_aju",
      key: "nomor_aju",
      width: 220,
      render: (text) => (
        <span style={{ fontWeight: 600, fontSize: 12 }}>
          {text?.replace(/(\d{6})(\d{6})(\d{8})(\d{6})/, "$1-$2-$3-$4")}
        </span>
      ),
    },
    {
      title: "No. Daftar",
      dataIndex: "nomor_daftar",
      key: "nomor_daftar",
      width: 100,
    },
    {
      title: "Tgl Daftar",
      dataIndex: "tanggal_daftar",
      key: "tanggal_daftar",
      width: 120,
      render: (text) => (text ? dayjs(text).format("DD MMM YYYY") : "-"),
    },
    {
      title: "Pemilik Barang",
      dataIndex: "nama_entitas",
      key: "nama_entitas",
      width: 200,
      ellipsis: true,
    },
    {
      title: "Respon",
      dataIndex: "nama_respon",
      key: "nama_respon",
      width: 120,
      render: (text) => {
        let color = "blue";
        if (text === "SPPB") color = "green";
        if (text === "DITOLAK") color = "red";
        if (text === "MENUNGGU RESPON") color = "orange";
        return (
          <Tag color={color} style={{ fontSize: 10 }}>
            {text}
          </Tag>
        );
      },
    },
    {
      title: "Aksi",
      key: "aksi",
      width: 140,
      render: (_, record) => (
        <Space size="small">
          <Button
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
            type="primary"
            size="small"
            style={{ fontSize: 11 }}
          >
            Detail
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => handleDownloadPdf(record)}
            size="small"
            style={{ fontSize: 11 }}
            disabled
          >
            PDF
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ marginTop: 10 }}>
      <Card bordered={false} bodyStyle={{ padding: "12px 0" }}>
        <Space style={{ marginBottom: 16, paddingLeft: 12 }}>
          <Input.Search
            placeholder="Cari Nomor Aju / Nama Pemilik..."
            onSearch={handleSearch}
            style={{ width: 300 }}
            allowClear
          />
        </Space>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          scroll={{ x: 930 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} dari ${total}`,
          }}
          onChange={handleTableChange}
          size="small"
          bordered
          style={{
            background: "white",
            borderRadius: 8,
            minWidth: 930,
          }}
        />
      </Card>

      <Modal
        title={
          <Space>
            <EyeOutlined /> Detail Dokumen Pabean
          </Space>
        }
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailVisible(false)}>
            Tutup
          </Button>,
        ]}
        width="95%"
        style={{ top: 10, maxWidth: 1000, padding: 0 }}
        bodyStyle={{ padding: 0, height: "80vh", overflowY: "hidden" }} // Full height modal for mobile
      >
        {detailLoading ? (
          <div style={{ padding: 24, textAlign: "center" }}>
            Loading details...
          </div>
        ) : (
          <Tabs
            defaultActiveKey="header"
            tabPosition="top"
            style={{ height: "100%" }}
          >
            {" "}
            {/* Changed to top for mobile */}
            <TabPane tab="Header" key="header">
              <div
                style={{
                  padding: 12,
                  overflowY: "auto",
                  maxHeight: "calc(80vh - 60px)",
                }}
              >
                {" "}
                {/* Scrollable content */}
                <Descriptions
                  title="Informasi Utama"
                  bordered
                  column={{ xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
                  size="small"
                  layout="vertical"
                >
                  {" "}
                  {/* Responsive columns */}
                  <Descriptions.Item label="Nomor Pengajuan">
                    {detailData.header.nomor_aju}
                  </Descriptions.Item>
                  <Descriptions.Item label="Nomor Pendaftaran">
                    {detailData.header.nomor_daftar}
                  </Descriptions.Item>
                  <Descriptions.Item label="Tanggal Pendaftaran">
                    {detailData.header.tanggal_daftar
                      ? dayjs(detailData.header.tanggal_daftar).format(
                          "DD MMMM YYYY",
                        )
                      : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Kode Dokumen">
                    {detailData.header.kode_dokumen}
                  </Descriptions.Item>
                  <Descriptions.Item label="Kantor Pabean">
                    {detailData.header.kode_kantor}
                  </Descriptions.Item>
                </Descriptions>
                <Divider style={{ margin: "12px 0" }} />
                <Descriptions
                  title="Keuangan"
                  bordered
                  column={{ xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
                  size="small"
                  layout="vertical"
                >
                  <Descriptions.Item label="Nilai CIF">
                    Rp{" "}
                    {Number(detailData.header.cif || 0).toLocaleString("id-ID")}
                  </Descriptions.Item>
                  <Descriptions.Item label="Bruto">
                    {detailData.header.bruto} Kg
                  </Descriptions.Item>
                  <Descriptions.Item label="Netto">
                    {detailData.header.netto} Kg
                  </Descriptions.Item>
                </Descriptions>
              </div>
            </TabPane>
            <TabPane tab="Entitas" key="entitas">
              <div style={{ padding: 12, overflowX: "auto" }}>
                <Table
                  dataSource={detailData.entitas}
                  columns={entitasColumns}
                  rowKey="seri"
                  pagination={false}
                  size="small"
                  scroll={{ x: true }}
                />
              </div>
            </TabPane>
            <TabPane tab="Barang" key="barang">
              <div style={{ padding: 12, overflowX: "auto" }}>
                <Table
                  dataSource={detailData.barang}
                  columns={barangColumns}
                  rowKey="seri_barang"
                  pagination={{ pageSize: 5 }}
                  size="small"
                  scroll={{ x: true }}
                />
              </div>
            </TabPane>
            <TabPane tab="Kemasan" key="kemasan">
              <div style={{ padding: 12, overflowX: "auto" }}>
                <Table
                  dataSource={detailData.kemasan}
                  columns={kemasanColumns}
                  rowKey="seri_kemasan"
                  pagination={false}
                  size="small"
                  scroll={{ x: true }}
                />
              </div>
            </TabPane>
            <TabPane tab="Kontainer" key="kontainer">
              <div style={{ padding: 12, overflowX: "auto" }}>
                <Table
                  dataSource={detailData.kontainer}
                  columns={kontainerColumns}
                  rowKey="seri_kontainer"
                  pagination={false}
                  size="small"
                  scroll={{ x: true }}
                />
              </div>
            </TabPane>
            <TabPane tab="Dokumen" key="dokumen">
              <div style={{ padding: 12, overflowX: "auto" }}>
                <Table
                  dataSource={detailData.dokumen}
                  columns={dokumenColumns}
                  rowKey="seri_dokumen"
                  pagination={false}
                  size="small"
                  scroll={{ x: true }}
                />
              </div>
            </TabPane>
            <TabPane tab="Pengangkut" key="pengangkut">
              <div
                style={{
                  padding: 12,
                  overflowY: "auto",
                  maxHeight: "calc(80vh - 60px)",
                }}
              >
                {detailData.pengangkut.map((p, idx) => (
                  <Descriptions
                    key={idx}
                    title={`Pengangkut #${idx + 1}`}
                    bordered
                    column={1}
                    size="small"
                    style={{ marginBottom: 16 }}
                  >
                    <Descriptions.Item label="Nama Pengangkut">
                      {p.nama_pengangkut}
                    </Descriptions.Item>
                    <Descriptions.Item label="Nomor Pengangkut">
                      {p.nomor_pengangkut}
                    </Descriptions.Item>
                    <Descriptions.Item label="Cara Angkut">
                      {p.kode_cara_angkut}
                    </Descriptions.Item>
                  </Descriptions>
                ))}
                {detailData.pengangkut.length === 0 && (
                  <div style={{ textAlign: "center", color: "#999" }}>
                    Tidak ada data pengangkut
                  </div>
                )}
              </div>
            </TabPane>
          </Tabs>
        )}
      </Modal>
    </div>
  );
};

export default IndexBrowse;
